import React, { FC, useContext, useEffect } from 'react';
import styles from './CreateGameForm.module.scss';
import { Button, Checkbox, CloseDark, Input, PlusLight, Select } from 'esa-ui-library';
import { Form } from '../../../components';
import { useStore } from '../../../../common';
import { PropertyType } from 'esa-ui-library/lib/types';
import { AuthContext } from '../../../../logic';
import { validateForm } from '../Games.helper';

interface CreateGameFormProps {
	isEdit: boolean;
	onValidityChange?: (isValid: boolean) => void;
}

const CreateGameForm: FC<CreateGameFormProps> = ({ isEdit, onValidityChange }): JSX.Element => {
	const [state, dispatch] = useStore();
	const { token } = useContext(AuthContext);
	const { gameForm, formErrors, success, providers, AAMS_types, selectedGame } = state;
	const [form, setForm] = React.useState(gameForm);

	React.useEffect(() => {
		setForm(gameForm);
	}, [gameForm]);

	const onChange = (field: string, val: string) => {
		setForm((prev: any) => {
			if (field.startsWith('custom_fields|')) {
				const [, id, prop] = field.split('|');
				return {
					...prev,
					custom_fields: prev.custom_fields?.map((f: any) => (f.id === id ? { ...f, [prop]: val } : f)),
				};
			}
			return { ...prev, [field]: val };
		});
		dispatch('FORM', { field, value: val });
	};

	const onSelect = (field: string, val: PropertyType[]) => {
		setForm((prev: any) => ({ ...prev, [field]: val }));
		dispatch('FORM', { field, value: val });
	};

	const onSwitch = (field: string, val: boolean) => {
		setForm((prev: any) => ({ ...prev, [field]: val }));
		dispatch('FORM', { field, value: val });
	};

	const addCustomField = () => {
		dispatch('ADD_FIELD');
	};

	const removeCustomField = (field: string) => {
		dispatch('REMOVE_FIELD', { field });
	};

	useEffect(() => {
		const errors = validateForm(gameForm);
		onValidityChange?.(!errors);
	}, [gameForm]);

	useEffect(() => {
		if (isEdit && selectedGame) {
			dispatch('GAME_INIT', { token, id: selectedGame.internal_game_id });
		}
	}, [isEdit, selectedGame]);

	return (
		<section>
			<Form success={success}>
				<div className={styles.CreateGameForm}>
					<Input
						className={styles.Input}
						type='text'
						label='Game Name'
						value={form.game_name}
						placeholder='Enter name'
						dataId='game-name-field'
						required
						onChange={(val) => onChange('game_name', val)}
						error={!!formErrors?.game_name}
						errorMessage={formErrors?.game_name}
					/>
					<Select
						className={styles.Input}
						label='Game provider'
						disabled={isEdit}
						options={providers}
						required
						placeholder='Select provider'
						selected={form.game_provider_id}
						dataId='provider-field'
						onChange={(val) => onSelect('game_provider_id', val)}
						error={!!formErrors?.game_provider_id}
						errorMessage={formErrors?.game_provider_id}
					/>
					<Input
						className={styles.Input}
						type='text'
						required
						label='External Game ID (desktop)'
						value={form.provider_game_id_desktop}
						placeholder='Enter id'
						dataId='external-game-id-desktop-field'
						onChange={(val) => onChange('provider_game_id_desktop', val)}
						error={!!formErrors?.provider_game_id_desktop}
						errorMessage={formErrors?.provider_game_id_desktop}
					/>
					<Input
						className={styles.Input}
						type='text'
						required
						label='External Game ID (mobile)'
						value={form.provider_game_id_mobile}
						placeholder='Enter id'
						dataId='external-game-id-mobile-field'
						onChange={(val) => onChange('provider_game_id_mobile', val)}
						error={!!formErrors?.provider_game_id_mobile}
						errorMessage={formErrors?.provider_game_id_mobile}
					/>
					<Input
						className={styles.Input}
						type='text'
						label='Notes'
						value={form.notes}
						placeholder='Notes'
						dataId='notes-field'
						onChange={(val) => onChange('notes', val)}
						error={!!formErrors?.notes}
						errorMessage={formErrors?.notes}
					/>
					{process.env.REACT_APP_SITE_TITLE?.includes('Italy') && (
						<Select
							className={styles.Input}
							label='AAMS Type'
							options={AAMS_types}
							required
							placeholder='Select AAMS Type'
							selected={form.aams_type_id}
							dataId='aams-field'
							onChange={(val) => onSelect('aams_type_id', val)}
							error={!!formErrors?.aams_type_id}
							errorMessage={formErrors?.aams_type_id}
						/>
					)}
					{process.env.REACT_APP_SITE_TITLE?.includes('Italy') && (
						<Input
							className={styles.Input}
							type='text'
							label='AAMS Game Id'
							value={form.aams_game_id}
							placeholder='Enter id'
							dataId='external-game-id-mobile-field'
							onChange={(val) => onChange('aams_game_id', val)}
							error={!!formErrors?.aams_game_id}
							errorMessage={formErrors?.aams_game_id}
						/>
					)}
					<Input
						className={styles.Input}
						type='number'
						label='RTP'
						value={form.rtp}
						placeholder='Enter rtp'
						dataId='rtp-field'
						onChange={(val) => onChange('rtp', val)}
						error={!!formErrors?.rtp}
						errorMessage={formErrors?.rtp}
					/>
					<Input
						className={styles.Input}
						type='number'
						label='Volatility'
						value={form.volatility}
						placeholder='Enter volatility'
						dataId='volatility-field'
						onChange={(val) => onChange('volatility', val)}
						error={!!formErrors?.volatility}
						errorMessage={formErrors?.volatility}
					/>
					<Input
						className={styles.Input}
						type='number'
						label='Wagering weight'
						value={form.wagering_weight}
						placeholder='Enter wagering weight'
						dataId='wagering-weight-field'
						onChange={(val) => onChange('wagering_weight', val)}
						error={!!formErrors?.wagering_weight}
						errorMessage={formErrors?.wagering_weight}
					/>
					<Input
						className={styles.Input}
						type='text'
						label='Custom Game Id'
						value={form.custom_game_id}
						placeholder='Custom Game Id'
						dataId='custom-game-id-field'
						onChange={(val) => onChange('custom_game_id', val)}
						error={!!formErrors?.custom_game_id}
						errorMessage={formErrors?.custom_game_id}
					/>
					<Checkbox
						className={styles.Checkbox}
						checked={form.is_slot}
						onChange={(val) => onSwitch('is_slot', val)}
						label='Slot'
					/>
					<Checkbox
						className={styles.Checkbox}
						checked={form.active}
						onChange={(val) => onSwitch('active', val)}
						label='Active'
					/>
					<div className={styles.CustomFieldsBox}>
						<div className={styles.CustomFieldsTitleContent}>
							<p className={styles.CustomFieldsTitle}>Custom fields:</p>
							<Button primary small onClick={addCustomField}>
								<PlusLight className={styles.AddUserIcon} />
								Add
							</Button>
						</div>
						{form.custom_fields?.map((field: PropertyType) => (
							<div key={field.id} className={styles.CustomFieldItem}>
								<div className={styles.CustomField}>
									<Input
										small
										className={styles.Input}
										type='text'
										label='Name'
										required
										value={field.name}
										placeholder='Enter name'
										onChange={(val) => onChange(`custom_fields|${field.id}|name`, val)}
										error={formErrors ? !!formErrors[field.id]?.name : false}
										errorMessage={formErrors ? formErrors[field.id]?.name : undefined}
									/>
									<Input
										small
										required
										className={styles.Input}
										type='text'
										label='Value'
										value={field.value}
										placeholder='Enter value'
										onChange={(val) => onChange(`custom_fields|${field.id}|value`, val)}
										error={formErrors ? !!formErrors[field.id]?.value : false}
										errorMessage={formErrors ? formErrors[field.id]?.value : undefined}
									/>
									<div>
										<CloseDark className={styles.RemoveIcon} size={24} onClick={() => removeCustomField(field.id)} />
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</Form>
		</section>
	);
};

export { CreateGameForm };
